import { MarkerType, Position, type Edge, type Node } from "@xyflow/react";

import type {
  DeviceRelationshipGraphResponse,
  DeviceResponse,
} from "@/api/frameworksApi";
import { validateIpAddress } from "@/utils/validators";

import {
  EDGE_LABEL_MAP,
  NODE_GAP,
  NODE_W,
  ROW_H,
  TOPO_EDGE_COLORS,
  type CiForm,
  type CiFormErrors,
  type TypeFieldConfig,
} from "./types";

export function parseApplicationList(row: DeviceResponse): string[] {
  const raw =
    row.applicationNames ??
    row.applications ??
    (row as { application?: string | string[] | null }).application;
  if (!raw) return [];
  if (Array.isArray(raw)) {
    const list = raw
      .map((item) => (typeof item === "string" ? item : String(item ?? "")))
      .map((s) => s.trim())
      .filter((s) => s.length > 0 && s !== "-");
    return Array.from(new Set(list));
  }
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed || trimmed === "-") return [];
    if (trimmed.includes(",")) {
      const list = trimmed
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0 && s !== "-");
      return Array.from(new Set(list));
    }
    return [trimmed];
  }
  return [];
}

export function toStringOptions(
  items?: (string | null | undefined)[],
): { value: string; label: string }[] {
  if (!Array.isArray(items)) return [];
  const set = new Set<string>();
  items.forEach((item) => {
    if (item?.trim()) set.add(item.trim());
  });
  return Array.from(set).map((val) => ({ value: val, label: val }));
}

export const formatDeviceType = (type: string): string => {
  if (type === "LB") return "LB";
  if (type === "VM") return "VM";
  return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
};

export function getSubmitButtonLabel(saving: boolean, isEditing: boolean): string {
  if (saving) {
    return isEditing ? "Updating…" : "Saving…";
  }
  return isEditing ? "Update" : "Save";
}

export function calculateFitCount(
  containerWidth: number,
  itemWidths: number[],
  badgeWidth: number = 64,
  gap: number = 6,
): number {
  if (containerWidth <= 0 || itemWidths.length === 0) return 1;

  let fitCount = 0;
  let accumulatedWidth = 0;

  for (let i = 0; i < itemWidths.length; i++) {
    const itemW = itemWidths[i] || 60;
    const isLast = i === itemWidths.length - 1;

    if (isLast) {
      if (accumulatedWidth + (i > 0 ? gap : 0) + itemW <= containerWidth) {
        fitCount = itemWidths.length;
        break;
      }
    }

    const remainingCount = itemWidths.length - (i + 1);
    const neededBadgeW = remainingCount > 0 ? gap + badgeWidth : 0;
    const candidateWidth =
      accumulatedWidth + (i > 0 ? gap : 0) + itemW + neededBadgeW;

    if (candidateWidth <= containerWidth) {
      accumulatedWidth += (i > 0 ? gap : 0) + itemW;
      fitCount = i + 1;
    } else {
      break;
    }
  }

  return Math.max(1, fitCount);
}

export interface AddNodeParams {
  id: string;
  name: string;
  type: string;
  status: string;
  row: number;
  colIndex: number;
  colTotal: number;
  isRoot: boolean;
}

export function buildDrawerGraph(data: DeviceRelationshipGraphResponse): {
  nodes: Node[];
  edges: Edge[];
} {
  const nodesMap = new Map<string, Node>();
  const edgesArr: Edge[] = [];
  const seen = new Set<string>();

  function addNode(params: AddNodeParams) {
    if (seen.has(params.id)) return;
    seen.add(params.id);
    const totalW = params.colTotal * (NODE_W + NODE_GAP) - NODE_GAP;
    const x = params.colIndex * (NODE_W + NODE_GAP) - totalW / 2;
    nodesMap.set(params.id, {
      id: params.id,
      type: "drawerDeviceNode",
      position: { x, y: params.row * ROW_H },
      sourcePosition: Position.Bottom,
      targetPosition: Position.Top,
      style: { width: NODE_W },
      data: {
        label: params.name,
        type: params.type,
        status: params.status,
        isRoot: params.isRoot,
      },
    });
  }

  const outgoing = [
    ...data.hostedOn.map((r) => ({
      ...r,
      edgeLabel: EDGE_LABEL_MAP.HOSTED_ON,
    })),
    ...data.connectedTo.map((r) => ({
      ...r,
      edgeLabel: EDGE_LABEL_MAP.CONNECTED_TO,
    })),
    ...data.behind.map((r) => ({ ...r, edgeLabel: EDGE_LABEL_MAP.BEHIND })),
    ...data.attachedTo.map((r) => ({
      ...r,
      edgeLabel: EDGE_LABEL_MAP.ATTACHED_TO,
    })),
    ...data.protectedBy.map((r) => ({
      ...r,
      edgeLabel: EDGE_LABEL_MAP.PROTECTED_BY,
    })),
  ];
  const incoming = [
    ...data.hosts.map((r) => ({ ...r, edgeLabel: EDGE_LABEL_MAP.HOSTED_ON })),
    ...data.connectedFrom.map((r) => ({
      ...r,
      edgeLabel: EDGE_LABEL_MAP.CONNECTED_TO,
    })),
    ...data.backends.map((r) => ({ ...r, edgeLabel: EDGE_LABEL_MAP.BEHIND })),
    ...data.attachedFrom.map((r) => ({
      ...r,
      edgeLabel: EDGE_LABEL_MAP.ATTACHED_TO,
    })),
    ...data.protects.map((r) => ({
      ...r,
      edgeLabel: EDGE_LABEL_MAP.PROTECTED_BY,
    })),
  ];

  const uniqueOut = Array.from(
    new Map(outgoing.map((r) => [r.peerCiId, r])).values(),
  );
  const uniqueIn = Array.from(
    new Map(incoming.map((r) => [r.peerCiId, r])).values(),
  );
  const maxCols = Math.max(uniqueOut.length, uniqueIn.length, 1);

  addNode({
    id: data.deviceId,
    name: data.deviceName,
    type: data.deviceType,
    status: data.deviceStatus,
    row: 1,
    colIndex: Math.floor(maxCols / 2),
    colTotal: maxCols,
    isRoot: true,
  });
  uniqueIn.forEach((r, i) =>
    addNode({
      id: r.peerCiId,
      name: r.peerName,
      type: r.peerType,
      status: r.peerStatus,
      row: 0,
      colIndex: i,
      colTotal: uniqueIn.length,
      isRoot: false,
    }),
  );
  uniqueOut.forEach((r, i) =>
    addNode({
      id: r.peerCiId,
      name: r.peerName,
      type: r.peerType,
      status: r.peerStatus,
      row: 2,
      colIndex: i,
      colTotal: uniqueOut.length,
      isRoot: false,
    }),
  );

  function makeEdge(
    id: string,
    source: string,
    target: string,
    label: string,
  ): Edge {
    const color = TOPO_EDGE_COLORS[label] ?? "#8b93ff";
    return {
      id,
      source,
      target,
      label,
      type: "bezier",
      animated: true,
      markerEnd: { type: MarkerType.ArrowClosed, color, width: 18, height: 18 },
      style: { stroke: color, strokeWidth: 2 },
      labelStyle: { fontSize: 10, fontWeight: 700, fill: color },
      labelBgStyle: { fill: "hsl(var(--card))", fillOpacity: 0.95 },
      labelBgPadding: [6, 3] as [number, number],
      labelBgBorderRadius: 6,
    };
  }

  outgoing.forEach((r) =>
    edgesArr.push(makeEdge(r.id, data.deviceId, r.peerCiId, r.edgeLabel)),
  );
  incoming.forEach((r) =>
    edgesArr.push(makeEdge(r.id, r.peerCiId, data.deviceId, r.edgeLabel)),
  );

  return { nodes: Array.from(nodesMap.values()), edges: edgesArr };
}

export function validateCiDeviceForm(
  form: CiForm,
  f: TypeFieldConfig,
): CiFormErrors {
  const errors: CiFormErrors = {};

  if (!form.orgId) errors.orgId = "Org unit is required";
  if (!form.name.trim()) errors.name = "Name is required";
  if (!form.type) errors.type = "Type is required";
  if (!form.status) errors.status = "Status is required";

  if (f.ipAddress) {
    if (!form.ipAddress.trim()) {
      errors.ipAddress = "IP Address is required";
    } else {
      const r = validateIpAddress(form.ipAddress.trim());
      if (!r.valid) errors.ipAddress = r.error;
    }
  }
  if (f.managementIp) {
    if (!form.managementIp.trim()) {
      errors.managementIp = "Management IP is required";
    } else {
      const r = validateIpAddress(form.managementIp.trim());
      if (!r.valid) errors.managementIp = r.error;
    }
  }
  if (f.virtualIp) {
    if (!form.virtualIp.trim()) {
      errors.virtualIp = "Virtual IP is required";
    } else {
      const r = validateIpAddress(form.virtualIp.trim());
      if (!r.valid) errors.virtualIp = r.error;
    }
  }
  if (f.hostname && !form.hostname.trim()) errors.hostname = "Hostname is required";
  if (f.osName && !form.osName.trim()) errors.osName = "OS Name is required";
  if (f.osVersion && !form.osVersion.trim()) errors.osVersion = "OS Version is required";
  if (f.hypervisor && !form.hypervisor) errors.hypervisor = "Hypervisor is required";
  if (f.parentCiId && !form.parentCiId) errors.parentCiId = "Parent server is required";
  if (f.imageName && !form.imageName.trim()) errors.imageName = "Image name is required";
  if (f.namespace && !form.namespace.trim()) errors.namespace = "Namespace is required";
  if (f.totalCapacityTb && !form.totalCapacityTb)
    errors.totalCapacityTb = "Total capacity is required";

  if (f.cpuCores && form.cpuCores) {
    const parsed = Number(form.cpuCores);
    if (!Number.isFinite(parsed) || parsed < 1 || !Number.isInteger(parsed))
      errors.cpuCores = "CPU cores must be a whole number ≥ 1";
  }
  if (f.ramGb && form.ramGb) {
    const parsed = Number(form.ramGb);
    if (!Number.isFinite(parsed) || parsed < 0)
      errors.ramGb = "RAM must be a number ≥ 0";
  }
  if (f.diskGb && form.diskGb) {
    const parsed = Number(form.diskGb);
    if (!Number.isFinite(parsed) || parsed < 0)
      errors.diskGb = "Disk must be a number ≥ 0";
  }
  if (f.totalCapacityTb && form.totalCapacityTb) {
    const parsed = Number(form.totalCapacityTb);
    if (!Number.isFinite(parsed) || parsed < 0)
      errors.totalCapacityTb = "Total capacity must be a number ≥ 0";
  }
  if (f.usedCapacityTb && form.usedCapacityTb) {
    const parsed = Number(form.usedCapacityTb);
    if (!Number.isFinite(parsed) || parsed < 0)
      errors.usedCapacityTb = "Used capacity must be a number ≥ 0";
  }

  return errors;
}
